import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  LayoutDashboard, FileText, Package, Ruler, Sparkles, Inbox,
  LogOut, Save, Plus, Trash2, ExternalLink, Search, X, Loader2, KeyRound, Download, Images, Upload,
} from "lucide-react";
import { LogoMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "page", label: "Page Content", icon: FileText },
  { id: "products", label: "Products", icon: Package },
  { id: "sizes", label: "Sizes", icon: Ruler },
  { id: "finishes", label: "Finishes", icon: Sparkles },
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "quotes", label: "Quote Requests", icon: Inbox },
  { id: "account", label: "Account", icon: KeyRound },
];

const STATUS_STYLES = {
  new: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  in_review: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  quoted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  closed: "bg-stone-500/15 text-stone-400 border-stone-500/30",
};

const STATUS_LABELS = { new: "New", in_review: "In review", quoted: "Quoted", closed: "Closed" };

const inputCls = "w-full bg-[#0E1116] border border-white/10 rounded-xl px-4 py-3 text-sm text-[#F5F3EF] transition-colors duration-300";
const labelCls = "block font-mono-spec text-[10px] uppercase tracking-[0.22em] text-[#666F7D] mb-2";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("overview");
  const [content, setContent] = useState(null);
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [quoteFilter, setQuoteFilter] = useState("");
  const [quoteSearch, setQuoteSearch] = useState("");
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newSize, setNewSize] = useState("");
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [galleryTarget, setGalleryTarget] = useState(null);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryFileRef = { current: null };

  const loadQuotes = useCallback(async (status = "", q = "") => {
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    const { data } = await axios.get(`${API}/admin/quotes`, { params, withCredentials: true });
    setQuotes(data);
  }, []);

  const loadStats = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
    setStats(data);
  }, []);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(async ({ data }) => {
        setUser(data);
        const [contentRes] = await Promise.all([
          axios.get(`${API}/content`),
          loadStats(),
          loadQuotes(),
        ]);
        setContent(contentRes.data);
      })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate, loadStats, loadQuotes]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    navigate("/admin/login");
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      const payload = { ...content };
      delete payload.key;
      payload.moq = Number(payload.moq);
      payload.lead_time_weeks = Number(payload.lead_time_weeks);
      payload.sizes = payload.sizes.map(Number).sort((a, b) => a - b);
      await axios.put(`${API}/admin/content`, payload, { withCredentials: true });
      toast.success("Published — the live page now shows your changes.");
      loadStats();
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const setC = (key, value) => setContent((c) => ({ ...c, [key]: value }));
  const setProduct = (i, key, value) => setContent((c) => {
    const products = [...c.products];
    products[i] = { ...products[i], [key]: value };
    return { ...c, products };
  });
  const setFinish = (i, key, value) => setContent((c) => {
    const finishes = [...c.finishes];
    finishes[i] = { ...finishes[i], [key]: value };
    return { ...c, finishes };
  });
  const setGalleryItem = (i, key, value) => setContent((c) => {
    const gallery = [...(c.gallery || [])];
    gallery[i] = { ...gallery[i], [key]: value };
    return { ...c, gallery };
  });
  const uploadGalleryImage = async (file) => {
    if (!file || galleryTarget === null) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Image is over 25 MB.");
    setGalleryUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/gallery-image`, fd, { withCredentials: true });
      setGalleryItem(galleryTarget, "url", data.url);
      toast.success("Photo uploaded — press Publish changes to show it on the site.");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed.");
    } finally {
      setGalleryUploading(false);
      setGalleryTarget(null);
    }
  };

  const addSize = () => {
    const n = parseInt(newSize, 10);
    if (!n || n < 10 || n > 500) return toast.error("Enter a diameter between 10 and 500 mm.");
    if (content.sizes.includes(n)) return toast.error("That diameter already exists.");
    setC("sizes", [...content.sizes, n].sort((a, b) => a - b));
    setNewSize("");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) return toast.error("New passwords do not match.");
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      }, { withCredentials: true });
      toast.success("Password updated. Use it next time you sign in.");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not change password.");
    } finally {
      setPwSaving(false);
    }
  };

  const setQuoteStatus = async (id, status) => {    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, { withCredentials: true });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      if (selectedQuote?.id === id) setSelectedQuote((q) => ({ ...q, status }));
      loadStats();
      toast.success(`Marked as ${STATUS_LABELS[status]}.`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (!user || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 size={26} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="admin-dashboard">
      <aside className="w-16 lg:w-60 shrink-0 border-r border-white/8 bg-[#0E1116] flex flex-col">
        <div className="p-4 lg:p-6 border-b border-white/8 flex items-center gap-3">
          <LogoMark size={30} />
          <div className="hidden lg:block leading-tight">
            <p className="text-[13px] font-semibold text-[#F5F3EF]">Studio Admin</p>
            <p className="text-[10px] font-mono-spec text-[#666F7D] tracking-widest uppercase">TubeBoxesDirect</p>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 lg:px-6 py-3 text-[13px] transition-colors duration-200 ${
                tab === t.id ? "text-[#E6CA65] bg-[#D4AF37]/8 border-r-2 border-[#D4AF37]" : "text-[#A1A8B3] hover:text-[#F5F3EF]"
              }`}
            >
              <t.icon size={17} />
              <span className="hidden lg:inline">{t.label}</span>
              {t.id === "quotes" && stats?.new_quotes > 0 && (
                <span className="hidden lg:flex ml-auto w-5 h-5 rounded-full bg-blue-500/20 text-blue-300 text-[10px] items-center justify-center">
                  {stats.new_quotes}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/8 flex flex-col gap-2">
          <Link to="/" className="flex items-center gap-3 text-[12px] text-[#666F7D] hover:text-[#D9A76A] px-2 py-2" data-testid="admin-view-site-link">
            <ExternalLink size={15} /> <span className="hidden lg:inline">View website</span>
          </Link>
          <button onClick={logout} className="flex items-center gap-3 text-[12px] text-[#666F7D] hover:text-red-300 px-2 py-2" data-testid="admin-logout-btn">
            <LogOut size={15} /> <span className="hidden lg:inline">Sign out</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-[#0B0D10]/85 backdrop-blur-xl border-b border-white/8 px-6 lg:px-10 h-16 flex items-center justify-between">
          <div>
            <h1 className="font-serif-display text-lg text-[#F5F3EF]">{TABS.find((t) => t.id === tab)?.label}</h1>
          </div>
          <div className="flex items-center gap-4">
            {["page", "products", "sizes", "finishes", "gallery"].includes(tab) && (
              <button onClick={saveContent} disabled={saving} data-testid="admin-save-btn"
                className="gold-btn text-[#0B0D10] text-[12px] font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {saving ? "Publishing…" : "Publish changes"}
              </button>
            )}
            <span className="text-[12px] text-[#666F7D] hidden sm:block" data-testid="admin-user-email">{user.email}</span>
          </div>
        </header>

        <div className="p-6 lg:p-10 max-w-5xl">
          {tab === "overview" && stats && (
            <div data-testid="admin-overview">
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { label: "Total quote requests", value: stats.total_quotes },
                  { label: "New requests", value: stats.new_quotes },
                  { label: "Catalogue products", value: stats.product_count },
                  { label: "Standard diameters", value: stats.size_count },
                  { label: "Current MOQ", value: `${stats.moq} units` },
                  { label: "Lead time", value: `from ${stats.lead_time_weeks} weeks` },
                ].map((s, i) => (
                  <motion.div key={s.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className="border border-white/8 bg-[#14171D] rounded-2xl p-6" data-testid={`stat-card-${i}`}>
                    <p className="font-mono-spec text-[10px] uppercase tracking-[0.2em] text-[#666F7D]">{s.label}</p>
                    <p className="mt-3 font-serif-display text-3xl foil-text font-semibold">{s.value}</p>
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 border border-amber-500/15 bg-[#D4AF37]/5 rounded-2xl p-6">
                <p className="text-sm text-[#D9A76A] leading-relaxed">
                  Everything on the public page is editable here — hero copy, MOQ, lead time, the three tube styles, all twelve diameters and the four finishes. Make changes in a tab, then press <strong>Publish changes</strong>.
                </p>
              </div>
            </div>
          )}

          {tab === "page" && (
            <div className="space-y-6" data-testid="admin-page-editor">
              <div>
                <label className={labelCls}>Announcement bar</label>
                <input value={content.announcement} onChange={(e) => setC("announcement", e.target.value)} className={inputCls} data-testid="edit-announcement" />
              </div>
              <div>
                <label className={labelCls}>Hero kicker (small line above heading)</label>
                <input value={content.hero_kicker} onChange={(e) => setC("hero_kicker", e.target.value)} className={inputCls} data-testid="edit-hero-kicker" />
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                {[["hero_line1", "Hero line 1"], ["hero_line2", "Hero line 2 (gold italic)"], ["hero_line3", "Hero line 3"]].map(([key, label]) => (
                  <div key={key}>
                    <label className={labelCls}>{label}</label>
                    <input value={content[key]} onChange={(e) => setC(key, e.target.value)} className={inputCls} data-testid={`edit-${key.replace(/_/g, "-")}`} />
                  </div>
                ))}
              </div>
              <div>
                <label className={labelCls}>Hero paragraph</label>
                <textarea value={content.hero_sub} onChange={(e) => setC("hero_sub", e.target.value)} rows={4} className={`${inputCls} resize-none`} data-testid="edit-hero-sub" />
              </div>
              <div className="grid sm:grid-cols-3 gap-5">
                <div>
                  <label className={labelCls}>MOQ (units)</label>
                  <input type="number" value={content.moq} onChange={(e) => setC("moq", e.target.value)} className={inputCls} data-testid="edit-moq" />
                </div>
                <div>
                  <label className={labelCls}>Lead time (weeks)</label>
                  <input type="number" value={content.lead_time_weeks} onChange={(e) => setC("lead_time_weeks", e.target.value)} className={inputCls} data-testid="edit-lead-time" />
                </div>
                <div>
                  <label className={labelCls}>Contact email</label>
                  <input value={content.contact_email} onChange={(e) => setC("contact_email", e.target.value)} className={inputCls} data-testid="edit-contact-email" />
                </div>
              </div>
            </div>
          )}

          {tab === "products" && (
            <div className="space-y-8" data-testid="admin-products-editor">
              {content.products.map((p, i) => (
                <div key={p.id} className="border border-white/8 bg-[#14171D] rounded-2xl p-6" data-testid={`admin-product-${p.id}`}>
                  <p className="font-mono-spec text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-5">Tube style {i + 1} — {p.id}</p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Name</label>
                      <input value={p.name} onChange={(e) => setProduct(i, "name", e.target.value)} className={inputCls} data-testid={`edit-product-${p.id}-name`} />
                    </div>
                    <div>
                      <label className={labelCls}>Tagline</label>
                      <input value={p.tagline} onChange={(e) => setProduct(i, "tagline", e.target.value)} className={inputCls} data-testid={`edit-product-${p.id}-tagline`} />
                    </div>
                  </div>
                  <div className="mt-5">
                    <label className={labelCls}>Description</label>
                    <textarea value={p.description} onChange={(e) => setProduct(i, "description", e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid={`edit-product-${p.id}-description`} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5 mt-5">
                    <div>
                      <label className={labelCls}>Best for</label>
                      <input value={p.best_for} onChange={(e) => setProduct(i, "best_for", e.target.value)} className={inputCls} data-testid={`edit-product-${p.id}-best-for`} />
                    </div>
                    <div>
                      <label className={labelCls}>Image URL</label>
                      <input value={p.image_url} onChange={(e) => setProduct(i, "image_url", e.target.value)} className={inputCls} data-testid={`edit-product-${p.id}-image`} />
                    </div>
                  </div>
                  {p.image_url && <img src={p.image_url} alt="" className="mt-4 h-24 w-40 object-cover rounded-lg border border-white/10" />}
                </div>
              ))}
            </div>
          )}

          {tab === "sizes" && (
            <div data-testid="admin-sizes-editor">
              <p className="text-sm text-[#A1A8B3] mb-6">These internal diameters (mm) appear in the size chart and the quote form. Remove any you no longer stock, or add a new standard.</p>
              <div className="flex flex-wrap gap-2.5 mb-8" data-testid="admin-size-list">
                {content.sizes.map((d) => (
                  <span key={d} className="group flex items-center gap-2 border border-amber-500/25 bg-[#14171D] rounded-full px-4 py-2 font-mono-spec text-xs text-[#D9A76A]" data-testid={`admin-size-${d}`}>
                    Ø {d} mm
                    <button onClick={() => setC("sizes", content.sizes.filter((x) => x !== d))} className="text-[#666F7D] hover:text-red-300" data-testid={`admin-size-remove-${d}`} aria-label={`Remove ${d}mm`}>
                      <Trash2 size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-3 max-w-sm">
                <input type="number" value={newSize} onChange={(e) => setNewSize(e.target.value)} placeholder="New diameter (mm)" className={inputCls} data-testid="admin-new-size-input" />
                <button onClick={addSize} className="gold-btn shrink-0 text-[#0B0D10] text-[12px] font-semibold px-5 rounded-xl flex items-center gap-1.5" data-testid="admin-add-size-btn">
                  <Plus size={14} /> Add
                </button>
              </div>
              <p className="mt-4 text-[11px] text-[#666F7D]">Remember to press Publish changes when done.</p>
            </div>
          )}

          {tab === "finishes" && (
            <div className="space-y-8" data-testid="admin-finishes-editor">
              {content.finishes.map((f, i) => (
                <div key={f.id} className="border border-white/8 bg-[#14171D] rounded-2xl p-6" data-testid={`admin-finish-${f.id}`}>
                  <p className="font-mono-spec text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-5">Finish {i + 1} — {f.id}</p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Name</label>
                      <input value={f.name} onChange={(e) => setFinish(i, "name", e.target.value)} className={inputCls} data-testid={`edit-finish-${f.id}-name`} />
                    </div>
                    <div>
                      <label className={labelCls}>Tagline</label>
                      <input value={f.tagline} onChange={(e) => setFinish(i, "tagline", e.target.value)} className={inputCls} data-testid={`edit-finish-${f.id}-tagline`} />
                    </div>
                  </div>
                  <div className="mt-5">
                    <label className={labelCls}>Description</label>
                    <textarea value={f.description} onChange={(e) => setFinish(i, "description", e.target.value)} rows={3} className={`${inputCls} resize-none`} data-testid={`edit-finish-${f.id}-description`} />
                  </div>
                  <div className="mt-5">
                    <label className={labelCls}>Highlight line</label>
                    <input value={f.highlight} onChange={(e) => setFinish(i, "highlight", e.target.value)} className={inputCls} data-testid={`edit-finish-${f.id}-highlight`} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "quotes" && (
            <div data-testid="admin-quotes-panel">
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1 max-w-sm">
                  <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666F7D]" />
                  <input
                    value={quoteSearch}
                    onChange={(e) => { setQuoteSearch(e.target.value); loadQuotes(quoteFilter, e.target.value); }}
                    placeholder="Search name, email, company…"
                    className={`${inputCls} pl-11`}
                    data-testid="admin-quote-search"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {["", "new", "in_review", "quoted", "closed"].map((s) => (
                    <button
                      key={s}
                      onClick={() => { setQuoteFilter(s); loadQuotes(s, quoteSearch); }}
                      data-testid={`admin-quote-filter-${s || "all"}`}
                      className={`text-[12px] font-medium rounded-full px-4 py-2 border transition-colors ${
                        quoteFilter === s ? "border-amber-500/60 bg-[#D4AF37]/12 text-[#E6CA65]" : "border-white/10 text-[#A1A8B3] hover:border-white/25"
                      }`}
                    >
                      {s ? STATUS_LABELS[s] : "All"}
                    </button>
                  ))}
                </div>
              </div>

              {quotes.length === 0 ? (
                <div className="border border-white/8 bg-[#14171D] rounded-2xl p-14 text-center" data-testid="admin-quotes-empty">
                  <Inbox size={28} className="mx-auto text-[#666F7D]" />
                  <p className="mt-4 text-sm text-[#A1A8B3]">No quote requests yet. They'll land here the moment a customer submits the form.</p>
                </div>
              ) : (
                <div className="border border-white/8 rounded-2xl overflow-hidden overflow-x-auto scrollbar-thin">
                  <table className="w-full text-sm min-w-[760px]" data-testid="admin-quotes-table">
                    <thead>
                      <tr className="bg-[#0E1116] text-left">
                        {["Client", "Specification", "Qty", "Date", "Status", ""].map((h) => (
                          <th key={h} className="px-5 py-4 font-mono-spec text-[10px] uppercase tracking-[0.18em] text-[#666F7D] font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {quotes.map((q) => (
                        <tr key={q.id} className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => setSelectedQuote(q)} data-testid={`quote-row-${q.id}`}>
                          <td className="px-5 py-4">
                            <p className="text-[#F5F3EF] font-medium">{q.name}</p>
                            <p className="text-[12px] text-[#666F7D]">{q.company || q.email}</p>
                          </td>
                          <td className="px-5 py-4 text-[#A1A8B3] text-[13px]">
                            {q.product_type} · {q.diameter_mm ? `Ø${q.diameter_mm}mm` : "size help"}{q.product_dims ? ` · ${q.product_dims}` : ""}
                            {q.finishes?.length > 0 && <span className="block text-[11px] text-[#D9A76A] mt-0.5">{q.finishes.join(" + ")}</span>}
                          </td>
                          <td className="px-5 py-4 font-mono-spec text-[13px] text-[#D9A76A]">{q.quantity}</td>
                          <td className="px-5 py-4 text-[12px] text-[#666F7D]">{new Date(q.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</td>
                          <td className="px-5 py-4">
                            <span className={`text-[11px] font-medium border rounded-full px-3 py-1 ${STATUS_STYLES[q.status]}`} data-testid={`quote-status-${q.id}`}>
                              {STATUS_LABELS[q.status]}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-[#666F7D] text-xs">View →</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "gallery" && (
            <div data-testid="admin-gallery-editor">
              <input
                ref={(el) => (galleryFileRef.current = el)}
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.gif,.avif"
                className="hidden"
                onChange={(e) => { uploadGalleryImage(e.target.files?.[0]); e.target.value = ""; }}
                data-testid="admin-gallery-file-input"
              />
              <p className="text-sm text-[#A1A8B3] mb-6">
                These photos fill the "Recent work" wall on the homepage. Upload a photo straight from your computer, or paste an image URL. The first image also sets the hero background — change it in Page Content.
              </p>
              <div className="grid sm:grid-cols-2 gap-5">
                {(content.gallery || []).map((g, i) => (
                  <div key={i} className="border border-white/8 bg-[#14171D] rounded-2xl p-5" data-testid={`admin-gallery-item-${i}`}>
                    {g.url ? (
                      <img src={g.url} alt="" className="h-40 w-full object-cover rounded-xl border border-white/10 mb-4" />
                    ) : (
                      <div className="h-40 w-full rounded-xl border border-dashed border-white/15 mb-4 flex items-center justify-center text-[#666F7D] text-xs">No image yet</div>
                    )}
                    <button
                      onClick={() => { setGalleryTarget(i); galleryFileRef.current?.click(); }}
                      disabled={galleryUploading}
                      className="ghost-btn w-full border border-dashed border-amber-500/30 rounded-xl px-4 py-3 text-[12px] font-medium text-[#D9A76A] flex items-center justify-center gap-2 mb-4 disabled:opacity-60"
                      data-testid={`admin-gallery-upload-${i}`}
                    >
                      {galleryUploading && galleryTarget === i ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {galleryUploading && galleryTarget === i ? "Uploading…" : "Upload photo from computer"}
                    </button>
                    <label className={labelCls}>Image URL</label>
                    <input value={g.url} onChange={(e) => setGalleryItem(i, "url", e.target.value)} className={`${inputCls} mb-4`} data-testid={`edit-gallery-${i}-url`} placeholder="https://…" />
                    <label className={labelCls}>Caption</label>
                    <input value={g.caption} onChange={(e) => setGalleryItem(i, "caption", e.target.value)} className={inputCls} data-testid={`edit-gallery-${i}-caption`} />
                    <button
                      onClick={() => setC("gallery", content.gallery.filter((_, x) => x !== i))}
                      className="mt-4 flex items-center gap-2 text-[12px] text-[#666F7D] hover:text-red-300 transition-colors"
                      data-testid={`admin-gallery-remove-${i}`}
                    >
                      <Trash2 size={13} /> Remove from wall
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setC("gallery", [...(content.gallery || []), { url: "", caption: "" }])}
                className="mt-6 gold-btn text-[#0B0D10] text-[12px] font-semibold px-5 py-3 rounded-full flex items-center gap-1.5"
                data-testid="admin-gallery-add-btn"
              >
                <Plus size={14} /> Add a photo
              </button>
              <p className="mt-4 text-[11px] text-[#666F7D]">Press Publish changes when done.</p>
            </div>
          )}

          {tab === "account" && (
            <div className="max-w-md" data-testid="admin-account-panel">
              <div className="border border-white/8 bg-[#14171D] rounded-2xl p-7">
                <p className="font-mono-spec text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Security</p>
                <h3 className="font-serif-display text-xl text-[#F5F3EF] mb-6">Change your password</h3>
                <form onSubmit={changePassword}>
                  <label className={labelCls}>Current password</label>
                  <input
                    type="password" value={pwForm.current_password} required
                    onChange={(e) => setPwForm((p) => ({ ...p, current_password: e.target.value }))}
                    className={`${inputCls} mb-5`} data-testid="pw-current-input"
                  />
                  <label className={labelCls}>New password (min 8 characters)</label>
                  <input
                    type="password" value={pwForm.new_password} required minLength={8}
                    onChange={(e) => setPwForm((p) => ({ ...p, new_password: e.target.value }))}
                    className={`${inputCls} mb-5`} data-testid="pw-new-input"
                  />
                  <label className={labelCls}>Repeat new password</label>
                  <input
                    type="password" value={pwForm.confirm} required minLength={8}
                    onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
                    className={`${inputCls} mb-7`} data-testid="pw-confirm-input"
                  />
                  <button type="submit" disabled={pwSaving} data-testid="pw-change-btn"
                    className="gold-btn w-full text-[#0B0D10] font-semibold text-sm py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
                    {pwSaving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
                    {pwSaving ? "Updating…" : "Update password"}
                  </button>
                </form>
              </div>
              <p className="mt-5 text-[12px] text-[#666F7D] leading-relaxed">
                Signed in as {user.email}. Your new password takes effect immediately — keep it somewhere safe, it can't be recovered from here.
              </p>
            </div>
          )}
        </div>
      </main>

      {selectedQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedQuote(null)} data-testid="quote-detail-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="w-full max-w-lg bg-[#14171D] border border-white/10 rounded-2xl p-7 max-h-[85vh] overflow-y-auto scrollbar-thin"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="font-mono-spec text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Quote request</p>
                <h3 className="font-serif-display text-xl text-[#F5F3EF] mt-1" data-testid="quote-detail-name">{selectedQuote.name}</h3>
              </div>
              <button onClick={() => setSelectedQuote(null)} className="text-[#666F7D] hover:text-[#F5F3EF] p-1" data-testid="quote-detail-close" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <dl className="space-y-3.5 text-sm">
              {[
                ["Email", selectedQuote.email], ["Phone", selectedQuote.phone || "—"], ["Company", selectedQuote.company || "—"],
                ["Tube style", selectedQuote.product_type],
                ["Diameter", selectedQuote.diameter_mm ? `Ø ${selectedQuote.diameter_mm} mm` : (selectedQuote.help_choosing ? "Help needed choosing" : "—")],
                ["Product dimensions", selectedQuote.product_dims || "—"],
                ["Quantity", selectedQuote.quantity],
                ["Finishes", selectedQuote.finishes?.length ? selectedQuote.finishes.join(", ") : "None selected"],
                ["Delivery country", selectedQuote.delivery_country || "—"],
                ["Postcode", selectedQuote.postcode || "—"],
                ["Required delivery date", selectedQuote.delivery_date || "—"],
                ["Source page", selectedQuote.source_page || "homepage"],
                ["Submitted", new Date(selectedQuote.created_at).toLocaleString("en-GB")],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-6 border-b border-white/5 pb-3">
                  <dt className="text-[#666F7D] text-[13px]">{k}</dt>
                  <dd className="text-[#F5F3EF] text-right">{v}</dd>
                </div>
              ))}
              {selectedQuote.message && (
                <div>
                  <dt className="text-[#666F7D] text-[13px] mb-1.5">Message</dt>
                  <dd className="text-[#A1A8B3] text-[13px] leading-relaxed bg-[#0E1116] rounded-lg p-4">{selectedQuote.message}</dd>
                </div>
              )}
              {selectedQuote.artworks?.length > 0 && (
                <div>
                  <dt className="text-[#666F7D] text-[13px] mb-1.5">Artwork files</dt>
                  <dd className="space-y-1.5" data-testid="quote-detail-artworks">
                    {selectedQuote.artworks.map((a) => (
                      <a
                        key={a.id}
                        href={`${API}/admin/files/${a.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-[13px] text-[#D9A76A] hover:text-[#E6CA65] bg-[#0E1116] rounded-lg px-4 py-2.5 transition-colors"
                        data-testid={`quote-artwork-download-${a.id}`}
                      >
                        <Download size={13} /> {a.filename}
                      </a>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
            <div className="mt-7">
              <p className={labelCls}>Update status</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS_LABELS).map(([s, label]) => (
                  <button
                    key={s}
                    onClick={() => setQuoteStatus(selectedQuote.id, s)}
                    data-testid={`quote-detail-status-${s}`}
                    className={`text-[12px] font-medium rounded-full px-4 py-2 border transition-colors ${
                      selectedQuote.status === s ? STATUS_STYLES[s] : "border-white/10 text-[#A1A8B3] hover:border-white/25"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
